import { DollarSign, ListChecks, ReceiptText } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";

interface BuilderLineItemsSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type LineItemsSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): LineItemsSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as LineItemsSettings)
    : {};
}

function getStringSetting(
  settings: LineItemsSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getNumberSetting(
  settings: LineItemsSettings,
  key: string,
  fallback: number
) {
  const value = settings[key];
  return typeof value === "number" ? value : fallback;
}

function getBooleanSetting(
  settings: LineItemsSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: LineItemsSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderLineItemsSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderLineItemsSettingsProps) {
  const settings = getSettings(page);

  const title = getStringSetting(
    settings,
    "title",
    page.title ?? "Services & Pricing"
  );
  const introText = getStringSetting(settings, "introText");
  const tableStyle = getStringSetting(settings, "tableStyle", "standard");
  const showDescriptions = getBooleanSetting(settings, "showDescriptions", true);
  const showQuantities = getBooleanSetting(settings, "showQuantities", true);
  const showUnits = getBooleanSetting(settings, "showUnits", true);
  const showPricingSummary = getBooleanSetting(
    settings,
    "showPricingSummary",
    true
  );
  const showTotals = getBooleanSetting(settings, "showTotals", true);
  const defaultTaxRate = getNumberSetting(settings, "defaultTaxRate", 0);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-emerald-500/10 via-teal-500/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <ReceiptText className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Services & Pricing Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Adjust how service rows, supporting details, summaries, and tax
              handling appear on the pricing page shown on the stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Page framing"
          description="Set the visible heading and optional intro above the pricing table."
        >
          <div>
            <FieldLabel
              label="Page title"
              icon={<ListChecks className="h-4 w-4" />}
              hint="Visible heading shown above the services table."
            />
            <input
              value={title}
              onChange={(e) =>
                onChange(updateSetting(settings, "title", e.target.value))
              }
              disabled={readOnly}
              placeholder="Services & Pricing"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Intro text"
              hint="Optional supporting copy shown before the line items table."
            />
            <textarea
              rows={4}
              value={introText}
              onChange={(e) =>
                onChange(updateSetting(settings, "introText", e.target.value))
              }
              disabled={readOnly}
              placeholder="Optional intro shown above the services table"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Table behavior"
          description="Choose how dense, compact, or descriptive the services table should feel."
        >
          <div>
            <FieldLabel
              label="Table style"
              hint="Use a tighter or more descriptive presentation depending on the proposal."
            />
            <select
              value={tableStyle}
              onChange={(e) =>
                onChange(updateSetting(settings, "tableStyle", e.target.value))
              }
              disabled={readOnly}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            >
              <option value="standard" className="bg-[#111]">
                Standard
              </option>
              <option value="compact" className="bg-[#111]">
                Compact
              </option>
              <option value="detailed" className="bg-[#111]">
                Detailed
              </option>
            </select>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
            <p className="text-xs leading-5 text-white/40">
              Use Standard for the default balance, Compact for tighter presentation,
              or Detailed when the proposal should include more explanatory service
              context.
            </p>
          </div>
        </SectionCard>

        <SectionCard
          title="Visible pricing details"
          description="Choose which line-item details and summary areas appear on the page."
        >
          <ToggleField
            label="Show service descriptions"
            description="Display descriptive text for each service row."
            checked={showDescriptions}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showDescriptions", checked))
            }
          />

          <ToggleField
            label="Show quantities"
            description="Include quantity values in the service table."
            checked={showQuantities}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showQuantities", checked))
            }
          />

          <ToggleField
            label="Show units"
            description="Include rate or unit labels like per visit or per month."
            checked={showUnits}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showUnits", checked))
            }
          />

          <ToggleField
            label="Show pricing summary"
            description="Display a summary block below the main service rows."
            checked={showPricingSummary}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showPricingSummary", checked))
            }
          />

          <ToggleField
            label="Show totals"
            description="Include subtotal, tax, and total values when available."
            checked={showTotals}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showTotals", checked))
            }
          />
        </SectionCard>

        <SectionCard
          title="Default tax handling"
          description="Set the default page-level tax rate used for pricing calculations."
        >
          <div>
            <FieldLabel
              label="Default tax rate"
              icon={<DollarSign className="h-4 w-4" />}
              hint="Use decimal format like 0.0825 for 8.25%."
            />
            <input
              type="number"
              min={0}
              max={1}
              step={0.0001}
              value={defaultTaxRate}
              onChange={(e) =>
                onChange(
                  updateSetting(
                    settings,
                    "defaultTaxRate",
                    Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : 0
                  )
                )
              }
              disabled={readOnly}
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}